/**
 * My account — staff member's own identity, voice number, notifications,
 * and passkeys.
 *
 * Two columns: the left is a single editable form (name/title/phone/TREC/
 * scheduling link, saved together with one "Save changes" button) plus
 * instant-apply notification toggles; the right is read-mostly status —
 * which number the AI agent and clients see, how it's formatted, and the
 * passkeys/sign-out that manage the SESSION rather than the profile data.
 * That split (batched edits vs. instant toggles) matches how each kind of
 * setting behaves: typos in your name shouldn't autosave keystroke by
 * keystroke, but "which number rings" should take effect the moment you
 * flip it.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  passkeyService,
  profilePhotoService,
  uploadProfilePhotoFile,
  type DeviceLinkMint,
  type PasskeyCredential,
} from "@re-platform/api-client";
import { Switch } from "@re-platform/ui";
import { useAuth } from "../lib/auth";
import { useRoles } from "../lib/roles";
import { api } from "../lib/api";
import "./Profile.css";

const PHOTO_ALLOWED_EXT = ["png", "jpg", "jpeg", "webp"];
const PHOTO_MAX_BYTES = 1_048_576;

const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super admin",
  brokerage_admin: "Broker / Admin",
  agent: "Agent",
};

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  phone_e164: string | null;
  primary_role: string;
  photo_url: string | null;
  display_title: string | null;
  scheduling_link: string | null;
  agent_id: string | null;
  tenant_slug: string | null;
  signup_link_origin: string | null;
  trec_license_number: string | null;
  public_phone_preference: "DID" | "PERSONAL" | null;
  did: string | null;
  did_label: string | null;
  did_display_text: string | null;
  did_display_format: "VANITY" | "ACTUAL" | null;
  did_shared: boolean;
}

interface NotificationPrefs {
  new_client_assigned: boolean;
  showing_requested: boolean;
  document_uploaded_validated: boolean;
  daily_activity_summary: boolean;
}

interface AccountForm {
  full_name: string;
  display_title: string;
  phone_e164: string;
  trec_license_number: string;
  scheduling_link: string;
}

function toForm(d: ProfileData | undefined): AccountForm {
  return {
    full_name: d?.full_name ?? "",
    display_title: d?.display_title ?? "",
    phone_e164: d?.phone_e164 ?? "",
    trec_license_number: d?.trec_license_number ?? "",
    scheduling_link: d?.scheduling_link ?? "",
  };
}

function describeCredential(cred: PasskeyCredential): string {
  if (cred.friendly_name) return cred.friendly_name;
  if (cred.transports?.includes("internal")) return "This device";
  if (cred.transports?.includes("usb")) return "Security key";
  return "Passkey";
}

export function Profile() {
  const { user, mode, logout } = useAuth();
  const { primary, roles } = useRoles();
  const qc = useQueryClient();

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  // ---- profile ----
  const profileQ = useQuery({
    queryKey: ["profile", "me"],
    queryFn: () => api.request<ProfileData>("/profile/me"),
  });
  const d = profileQ.data;

  const [form, setForm] = useState<AccountForm>(() => toForm(undefined));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (d && !dirty) setForm(toForm(d));
  }, [d, dirty]);

  function setField<K extends keyof AccountForm>(
    key: K,
    value: AccountForm[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  const saveAccount = useMutation({
    mutationFn: () =>
      api.request<ProfileData>("/profile/me", {
        method: "PATCH",
        body: {
          full_name: form.full_name || null,
          display_title: form.display_title || null,
          phone_e164: form.phone_e164 || null,
          trec_license_number: form.trec_license_number || null,
          scheduling_link: form.scheduling_link || null,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profile", "me"] });
      setDirty(false);
      showToast("Account saved.", true);
    },
    onError: () => showToast("Failed to save changes.", false),
  });

  // Phone/format toggles on the right apply immediately — no shared "dirty"
  // state with the left form, so flipping one never blocks or clobbers an
  // in-progress edit on the other.
  const patchProfile = useMutation({
    mutationFn: (body: {
      public_phone_preference?: "DID" | "PERSONAL";
      did_display_format?: "VANITY" | "ACTUAL";
    }) => api.request<ProfileData>("/profile/me", { method: "PATCH", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profile", "me"] });
    },
    onError: () => showToast("Failed to update your number settings.", false),
  });

  // ---- photo ----
  const photoInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!PHOTO_ALLOWED_EXT.includes(ext))
        throw new Error(
          `Unsupported file type — allowed: ${PHOTO_ALLOWED_EXT.join(", ")}`,
        );
      if (file.size > PHOTO_MAX_BYTES)
        throw new Error(`Photo exceeds the ${PHOTO_MAX_BYTES / 1024}KB limit`);
      const presigned = await profilePhotoService.presignUpload(api, ext);
      await uploadProfilePhotoFile(
        presigned.upload_url,
        presigned.content_type,
        file,
      );
      return profilePhotoService.confirmUpload<ProfileData>(
        api,
        presigned.s3_key,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profile", "me"] });
      showToast("Photo uploaded.", true);
    },
    onError: (err: Error) => showToast(err.message, false),
  });

  // ---- notifications ----
  const notifQ = useQuery({
    queryKey: ["profile", "me", "notifications"],
    queryFn: () => api.request<NotificationPrefs>("/profile/me/notifications"),
  });

  const patchNotif = useMutation({
    mutationFn: (body: Partial<NotificationPrefs>) =>
      api.request<NotificationPrefs>("/profile/me/notifications", {
        method: "PATCH",
        body,
      }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ["profile", "me", "notifications"] });
      const previous = qc.getQueryData<NotificationPrefs>([
        "profile",
        "me",
        "notifications",
      ]);
      if (previous) {
        qc.setQueryData(["profile", "me", "notifications"], {
          ...previous,
          ...body,
        });
      }
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous) {
        qc.setQueryData(["profile", "me", "notifications"], context.previous);
      }
      showToast("Failed to update that notification.", false);
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: ["profile", "me", "notifications"],
      });
    },
  });

  // ---- passkeys ----
  const credsQ = useQuery({
    queryKey: ["passkey-credentials"],
    queryFn: () => passkeyService.listCredentials(api),
    retry: false,
  });
  const [mint, setMint] = useState<DeviceLinkMint | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [signupCopied, setSignupCopied] = useState(false);
  const addDevice = useMutation({
    mutationFn: () => passkeyService.mintDeviceLink(api),
    onSuccess: (data) => {
      setLinkCopied(false);
      setMint(data);
    },
    onError: () => showToast("Couldn't create an add-device link.", false),
  });
  const removeCredential = useMutation({
    mutationFn: (id: string) => passkeyService.deleteCredential(api, id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["passkey-credentials"] }),
    onError: (err: unknown) =>
      showToast(
        err instanceof Error ? err.message : "Couldn't remove that device.",
        false,
      ),
  });
  const credentials = credsQ.data ?? [];

  // ---- derived display ----
  const name = d?.full_name ?? user?.name ?? user?.email ?? "Staff member";
  const email = d?.email ?? user?.email ?? (mode === "dev" ? "—" : null);
  const photo = d?.photo_url ?? user?.picture;
  const initial = (name[0] ?? "?").toUpperCase();
  const primaryLabel = primary ? (ROLE_LABEL[primary] ?? primary) : "Staff";
  const isAgent = primary === "agent" && !!d?.agent_id;

  // Spec 023 (US6) — the agent's never-expiring sign-up link: their own page on the
  // CLIENT app, which carries no invite token, so nothing about it can expire. #117
  // deleted this card; profile-svc never stopped returning its parts (`agent_id` is
  // the agents.id the page route resolves). The base is the agent's verified custom
  // domain, else the client app — never this admin page's own origin, which is why
  // FormDetail's window.location fallback is not reused here.
  const signupLink =
    isAgent && d?.tenant_slug
      ? `${(
          d.signup_link_origin ??
          (import.meta.env.VITE_WEB_APP_BASE_URL as string | undefined) ??
          "https://app.newbrainllc.us"
        ).replace(/\/$/, "")}/b/${d.tenant_slug}/a/${d.agent_id}`
      : null;

  const displayFormat = d?.did_display_format ?? "VANITY";
  const clientsSeeText =
    displayFormat === "VANITY" && d?.did_display_text
      ? d.did_display_text
      : (d?.did ?? "");

  return (
    <div className="acct">
      {toast && (
        <div
          className={`acct__toast ${toast.ok ? "acct__toast--ok" : "acct__toast--err"}`}
        >
          {toast.msg}
        </div>
      )}

      <div className="acct__intro">
        <h1 className="acct__title">My account</h1>
        <p className="acct__subtitle">
          Update your profile, voice number, and notifications — this controls
          how clients and the AI voice agent reach you.
        </p>
      </div>

      <div className="acct-grid">
        {/* ================= LEFT COLUMN ================= */}
        <div className="acct-col">
          <section className="rp-card rp-card--pad-md acct-card">
            <div className="acct-identity">
              <div className="acct-avatar">
                {photo ? <img src={photo} alt={name} /> : initial}
              </div>
              <div className="acct-identity__meta">
                <div className="acct-identity__name">{name}</div>
                <div className="acct-identity__badges">
                  <span className="rp-pill rp-pill--info">
                    {primaryLabel.toUpperCase()}
                  </span>
                  {roles
                    .filter((r) => r !== primary)
                    .map((r) => (
                      <span key={r} className="rp-pill rp-pill--neutral">
                        {ROLE_LABEL[r] ?? r}
                      </span>
                    ))}
                  {isAgent && d?.trec_license_number && (
                    <span className="acct-identity__license">
                      REALTOR® · TREC #{d.trec_license_number}
                    </span>
                  )}
                </div>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept={PHOTO_ALLOWED_EXT.map((e) => `.${e}`).join(",")}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadPhoto.mutate(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="rp-btn rp-btn--secondary rp-btn--sm"
                disabled={uploadPhoto.isPending}
                onClick={() => photoInputRef.current?.click()}
              >
                {uploadPhoto.isPending ? "Uploading…" : "Change photo"}
              </button>
            </div>

            <form
              className="acct-form"
              onSubmit={(e) => {
                e.preventDefault();
                saveAccount.mutate();
              }}
            >
              <div className="acct-form__grid">
                <label className="rp-field">
                  <span className="rp-field__label">Full name</span>
                  <input
                    className="rp-field__input"
                    value={form.full_name}
                    onChange={(e) => setField("full_name", e.target.value)}
                  />
                </label>
                <label className="rp-field">
                  <span className="rp-field__label">Display title</span>
                  <input
                    className="rp-field__input"
                    placeholder="e.g. Broker / Owner"
                    value={form.display_title}
                    onChange={(e) => setField("display_title", e.target.value)}
                  />
                </label>
                <label className="rp-field">
                  <span className="rp-field__label">Email</span>
                  <input
                    className="rp-field__input"
                    value={email ?? ""}
                    disabled
                    readOnly
                  />
                </label>
                <label className="rp-field">
                  <span className="rp-field__label">Mobile phone</span>
                  <input
                    className="rp-field__input"
                    type="tel"
                    placeholder="+12125551234"
                    value={form.phone_e164}
                    onChange={(e) => setField("phone_e164", e.target.value)}
                  />
                </label>
                {isAgent && (
                  <label className="rp-field">
                    <span className="rp-field__label">TREC license #</span>
                    <input
                      className="rp-field__input"
                      value={form.trec_license_number}
                      onChange={(e) =>
                        setField("trec_license_number", e.target.value)
                      }
                    />
                  </label>
                )}
                <label className="rp-field">
                  <span className="rp-field__label">Scheduling link</span>
                  <input
                    className="rp-field__input"
                    placeholder="cal.com/you"
                    value={form.scheduling_link}
                    onChange={(e) =>
                      setField("scheduling_link", e.target.value)
                    }
                  />
                </label>
              </div>

              <button
                type="submit"
                className="rp-btn rp-btn--primary rp-btn--md"
                disabled={!dirty || saveAccount.isPending}
              >
                {saveAccount.isPending ? "Saving…" : "Save changes"}
              </button>
            </form>
          </section>

          {signupLink && (
            <section className="rp-card rp-card--pad-md acct-card">
              <div className="rp-card__header">
                <h2 className="rp-card__title">
                  Your sign-up link — share anywhere, never expires
                </h2>
                <p className="rp-card__subtitle">
                  Post it once on Facebook, Instagram or a business card. Anyone
                  who opens it can sign up as your client.
                </p>
              </div>
              <div className="acct-mint__row">
                <code>{signupLink}</code>
                <button
                  type="button"
                  className="rp-btn rp-btn--secondary rp-btn--sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(signupLink);
                    setSignupCopied(true);
                    window.setTimeout(() => setSignupCopied(false), 2000);
                  }}
                >
                  {signupCopied ? "Copied ✓" : "Copy"}
                </button>
              </div>
            </section>
          )}

          <section className="rp-card rp-card--pad-md acct-card">
            <div className="rp-card__header">
              <h2 className="rp-card__title">Notifications</h2>
              <p className="rp-card__subtitle">
                Choose what pings you and how.
              </p>
            </div>

            {notifQ.data && (
              <div className="acct-notif-list">
                {(
                  [
                    ["new_client_assigned", "New client assigned to me"],
                    ["showing_requested", "Showing requested"],
                    [
                      "document_uploaded_validated",
                      "Document uploaded & validated",
                    ],
                    ["daily_activity_summary", "Daily activity summary"],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key} className="acct-notif-row">
                    <span className="acct-notif-row__label">{label}</span>
                    <Switch
                      aria-label={label}
                      checked={notifQ.data[key]}
                      disabled={patchNotif.isPending}
                      onCheckedChange={(checked) =>
                        patchNotif.mutate({ [key]: checked })
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* ================= RIGHT COLUMN ================= */}
        <div className="acct-col">
          <section className="rp-card rp-card--pad-md acct-card">
            <div className="rp-card__header">
              <h2 className="rp-card__title">My numbers</h2>
              <p className="rp-card__subtitle">
                {isAgent
                  ? "You have a personal line and a dedicated DID answered by your AI agent. Choose which one clients see and how it's formatted."
                  : "The number the voice agent uses to recognise you when you call in."}
              </p>
            </div>

            <div className="acct-numline">
              <span className="acct-numline__icon" aria-hidden>
                📞
              </span>
              <div className="acct-numline__meta">
                <div className="acct-numline__label">Personal line</div>
                <div className="acct-numline__value">
                  {d?.phone_e164 ?? "Not set"}
                </div>
              </div>
            </div>

            {isAgent && (
              <>
                <div className="acct-numline">
                  <span className="acct-numline__icon" aria-hidden>
                    ➕
                  </span>
                  <div className="acct-numline__meta">
                    <div className="acct-numline__label">
                      DID line{d?.did ? " · AI agent answers" : ""}
                    </div>
                    <div className="acct-numline__value">
                      {d?.did ?? "Not assigned yet"}
                    </div>
                  </div>
                  {d?.did && !d.did_shared && (
                    <span className="rp-pill rp-pill--success">Active</span>
                  )}
                </div>

                {d?.did && (
                  <>
                    <div className="acct-divider" />

                    <div className="acct-toggle-label">
                      Number shown to clients
                    </div>
                    <div className="acct-seg">
                      <button
                        type="button"
                        className={
                          d.public_phone_preference === "PERSONAL"
                            ? "acct-seg--on"
                            : ""
                        }
                        disabled={patchProfile.isPending || d.did_shared}
                        onClick={() =>
                          patchProfile.mutate({
                            public_phone_preference: "PERSONAL",
                          })
                        }
                      >
                        Personal
                      </button>
                      <button
                        type="button"
                        className={
                          d.public_phone_preference === "DID"
                            ? "acct-seg--on"
                            : ""
                        }
                        disabled={patchProfile.isPending || d.did_shared}
                        onClick={() =>
                          patchProfile.mutate({
                            public_phone_preference: "DID",
                          })
                        }
                      >
                        DID
                      </button>
                    </div>

                    {!d.did_shared && (
                      <>
                        <div className="acct-toggle-label">Display format</div>
                        <div className="acct-seg">
                          <button
                            type="button"
                            className={
                              displayFormat === "VANITY" ? "acct-seg--on" : ""
                            }
                            disabled={patchProfile.isPending}
                            onClick={() =>
                              patchProfile.mutate({
                                did_display_format: "VANITY",
                              })
                            }
                          >
                            Vanity
                          </button>
                          <button
                            type="button"
                            className={
                              displayFormat === "ACTUAL" ? "acct-seg--on" : ""
                            }
                            disabled={patchProfile.isPending}
                            onClick={() =>
                              patchProfile.mutate({
                                did_display_format: "ACTUAL",
                              })
                            }
                          >
                            Actual number
                          </button>
                        </div>
                      </>
                    )}

                    <div className="acct-preview">
                      <span className="acct-preview__icon" aria-hidden>
                        👁
                      </span>
                      <div>
                        <div className="acct-preview__label">Clients see</div>
                        <div className="acct-preview__value">
                          {clientsSeeText}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          <section className="rp-card rp-card--pad-md acct-card">
            <div className="acct-card__row-header">
              <h2 className="rp-card__title">Passkeys</h2>
              <button
                type="button"
                className="rp-btn rp-btn--secondary rp-btn--sm"
                disabled={addDevice.isPending}
                onClick={() => addDevice.mutate()}
              >
                {addDevice.isPending ? "Preparing…" : "+ Add"}
              </button>
            </div>

            {credsQ.isLoading ? (
              <p className="acct-hint">Loading…</p>
            ) : (
              credentials.map((cred) => (
                <div key={cred.id} className="acct-passkey-row">
                  <span className="acct-passkey-row__icon" aria-hidden>
                    🔒
                  </span>
                  <div className="acct-passkey-row__meta">
                    <div className="acct-passkey-row__name">
                      {describeCredential(cred)}
                    </div>
                    <div className="acct-passkey-row__sub">
                      {cred.last_used_at
                        ? `Last used ${cred.last_used_at.slice(0, 10)}`
                        : "Not used yet"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rp-btn rp-btn--ghost rp-btn--sm"
                    disabled={
                      credentials.length <= 1 || removeCredential.isPending
                    }
                    title={
                      credentials.length <= 1
                        ? "Add another device before removing this one"
                        : "Remove this device"
                    }
                    onClick={() => removeCredential.mutate(cred.id)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}

            {mint && (
              <div className="acct-mint">
                <p className="acct-hint">
                  Open this link on the device you want to add — it works once
                  and expires in {Math.round(mint.expires_in / 60)} minutes.
                </p>
                <div className="acct-mint__row">
                  <code>{mint.add_device_url}</code>
                  <button
                    type="button"
                    className="rp-btn rp-btn--secondary rp-btn--sm"
                    onClick={() => {
                      void navigator.clipboard?.writeText(mint.add_device_url);
                      setLinkCopied(true);
                    }}
                  >
                    {linkCopied ? "Copied ✓" : "Copy link"}
                  </button>
                  <button
                    type="button"
                    className="rp-btn rp-btn--ghost rp-btn--sm"
                    onClick={() => setMint(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </section>

          {mode === "auth0" && (
            <section className="rp-card rp-card--pad-md acct-card acct-signout">
              <button
                type="button"
                className="acct-signout__btn"
                onClick={logout}
              >
                <span aria-hidden>⏻</span> Sign out
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
